import { apiService, getApiErrorMessage, Video } from '../../services/api';
import { segmentCache } from '../../services/segmentCache';
import { generateIdempotencyKey } from '../../services/idempotency';
import { toGallerySearchResult } from '../search-workspace/useBrowseGallery';
import type { StudioSearchCatalog, StudioSearchTransport } from './types';

/** Container-scoped latest analyses; cached catalogs contain durable references only. */
export const createWebStudioSearchTransport = (containerIndexId = 'playground'): StudioSearchTransport => {
  let library: StudioSearchCatalog | null = null;
  let generation = 0;
  let searchAttempt: { signature: string; key: string } | null = null;
  const scopes = new Map<string, StudioSearchCatalog>();
  const readScope = async (videos: Video[], videoIds?: string[], signal?: AbortSignal): Promise<StudioSearchCatalog> => {
    try {
      const scope = await apiService.getStudioSearchScope(videoIds, signal, containerIndexId);
      const processed = new Set(scope.browse_targets.map(target => target.video_id));
      return { ...scope, videos: videos.filter(video => processed.has(video.video_id)) };
    } catch (error) {
      if (signal?.aborted) throw error;
      // Preserve the video picker when search scope limits require narrowing.
      return { videos, runs: [], browse_targets: [], fields: [], warnings: [],
        scopeError: getApiErrorMessage(error, 'Could not load this search scope. Select fewer videos or retry.') };
    }
  };
  const ensureLibrary = async (signal?: AbortSignal) => {
    if (library) return library;
    const requestGeneration = generation;
    const videos: Video[] = [];
    let cursor: string | undefined;
    do {
      const page = containerIndexId === 'playground' ? await apiService.getPlaygroundVideos(100, cursor, signal)
        : await apiService.getVideosInIndex(containerIndexId, 100, cursor);
      videos.push(...page.data);
      cursor = page.pagination?.has_more ? page.pagination.next_cursor || undefined : undefined;
    } while (cursor && !signal?.aborted);
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const pages: StudioSearchCatalog[] = [];
    for (let offset = 0; offset < videos.length; offset += 100) {
      const chunk = videos.slice(offset, offset + 100);
      pages.push(await readScope(chunk, chunk.map(video => video.video_id), signal));
    }
    const next: StudioSearchCatalog = { container: containerIndexId === 'playground' ? { type: 'playground' } : { type: 'index', index_id: containerIndexId }, videos: pages.flatMap(page => page.videos),
      runs: Array.from(new Map(pages.flatMap(page => page.runs).map(run => [run.run_id, run])).values()).map(run => ({ ...run, video_ids: Array.from(new Set(pages.flatMap(page => page.runs).filter(item => item.run_id === run.run_id).flatMap(item => item.video_ids))) })),
      browse_targets: pages.flatMap(page => page.browse_targets),
      fields: Array.from(new Map(pages.flatMap(page => page.fields).map(field => [field.name, field])).values()),
      warnings: Array.from(new Set(pages.flatMap(page => page.warnings ?? []))),
      scopeError: pages.find(page => page.scopeError)?.scopeError,
    };
    if (next.videos.length > 100) next.scopeError = 'Search supports up to 100 selected videos. Narrow the video scope to continue.';
    if (signal?.aborted || requestGeneration !== generation) throw new DOMException('Aborted', 'AbortError');
    library = next;
    return next;
  };
  return {
    invalidateCatalog: () => { generation += 1; library = null; scopes.clear(); },
    catalog: async ({ videoIds, signal }) => {
      const all = await ensureLibrary(signal);
      if (videoIds === undefined) return all;
      if (!videoIds.length) return { ...all, browse_targets: [], fields: [], warnings: [], scopeError: undefined };
      const key = JSON.stringify([...videoIds].sort());
      const cached = scopes.get(key);
      if (cached) return cached;
      const scoped = { ...await readScope(all.videos as Video[], videoIds, signal), videos: all.videos };
      if (!scoped.scopeError && !signal?.aborted) {
        scopes.set(key, scoped);
        if (scopes.size > 20) scopes.delete(scopes.keys().next().value as string);
      }
      return scoped;
    },
    browse: async ({ targets, signal }) => {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      // Explicit limits select the existing lightweight gallery projection.
      const pages = await segmentCache.loadBatchSegments(targets.map(target => ({ ...target, limit: target.limit || 12 })));
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      return pages.map((page) => {
        const video = library?.videos.find((candidate) => candidate.video_id === page.video_id) as Video | undefined;
        if (!page.run_id) throw new Error('Segment results did not identify their execution. Please refresh the scope.');
        return { video_id: page.video_id, run_id: page.run_id, nextCursor: page.next_cursor || null,
          results: video ? page.segments.map((segment) => toGallerySearchResult(segment, video, page.run_id!, containerIndexId)) : [],
        };
      });
    },
    search: async (options) => {
      const signature = JSON.stringify([[...options.videoIds].sort(), options.query.trim(), options.filters]);
      if (searchAttempt?.signature !== signature) searchAttempt = { signature, key: generateIdempotencyKey('studio-search') };
      const attempt = searchAttempt;
      const page = await apiService.searchStudioVideos({ ...options, containerIndexId, idempotencyKey: attempt.key });
      if (searchAttempt === attempt) searchAttempt = null;
      return page;
    },
    page: ({ snapshotId, cursor, signal }) => apiService.getStudioSearchPage(snapshotId, cursor, signal),
  };
};
